import { Box } from '@mui/material';
import { useLayoutEffect, useRef } from 'react';
import Vex from 'vexflow';

const DIVISIONS_PER_WHOLE_NOTE = 16;
const VEXFLOW_NOTE_ICON_WIDTH = 72;
const VEXFLOW_NOTE_ICON_HEIGHT = 54;
const VEXFLOW_NOTE_STAVE_X = 14;
const VEXFLOW_NOTE_STAVE_Y = -17;
const VEXFLOW_NOTE_STAVE_WIDTH = 56;
const VEXFLOW_NOTE_FORMAT_WIDTH = 38;
const VEXFLOW_NOTE_ANCHOR_X = 38;
const VEXFLOW_NOTE_ANCHOR_Y = 43;
const NOTE_PALETTE_GLYPH_SCALE = 1.22;

const getVexFlowNoteGlyphDuration = (
  durationDivisions: number,
): { duration: string; dots: number } => {
  if (durationDivisions <= 1) return { duration: '16', dots: 0 };
  if (durationDivisions <= 2) return { duration: '8', dots: 0 };
  if (durationDivisions === 6) return { duration: 'q', dots: 1 };
  if (durationDivisions === 8) return { duration: 'h', dots: 0 };
  if (durationDivisions >= DIVISIONS_PER_WHOLE_NOTE) return { duration: 'w', dots: 0 };
  return { duration: 'q', dots: 0 };
};

const removeElementChildren = (element: HTMLElement) => {
  while (element.firstChild) {
    element.removeChild(element.firstChild);
  }
};

type VexFlowRenderedNoteGlyphProps = {
  durationDivisions: number;
  fillColor?: string;
  stemColor?: string;
};

const VexFlowRenderedNoteGlyph = ({
  durationDivisions,
  fillColor = 'currentColor',
  stemColor = 'currentColor',
}: VexFlowRenderedNoteGlyphProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    removeElementChildren(container);

    try {
      const VF = Vex.Flow;
      const renderer = new VF.Renderer(container, VF.Renderer.Backends.SVG);
      renderer.resize(VEXFLOW_NOTE_ICON_WIDTH, VEXFLOW_NOTE_ICON_HEIGHT);

      const context = renderer.getContext();
      const stave = new VF.Stave(
        VEXFLOW_NOTE_STAVE_X,
        VEXFLOW_NOTE_STAVE_Y,
        VEXFLOW_NOTE_STAVE_WIDTH,
        {
          left_bar: false,
          right_bar: false,
        },
      );
      const { duration, dots } = getVexFlowNoteGlyphDuration(durationDivisions);
      const note = new VF.StaveNote({
        clef: 'treble',
        keys: ['b/4'],
        duration,
        stem_direction: VF.Stem.UP,
      });
      const noteStyle = {
        fillStyle: fillColor,
        strokeStyle: stemColor,
      };

      note.setStyle(noteStyle);
      note.setStemStyle(noteStyle);
      note.setFlagStyle(noteStyle);
      for (let dotIndex = 0; dotIndex < dots; dotIndex += 1) {
        note.addDotToAll();
      }

      const voice = new VF.Voice({ num_beats: 1, beat_value: 4 }).setStrict(false);
      voice.addTickables([note]);
      new VF.Formatter().joinVoices([voice]).format([voice], VEXFLOW_NOTE_FORMAT_WIDTH);
      voice.draw(context, stave);

      const svgElement = container.querySelector('svg');
      if (svgElement) {
        svgElement.setAttribute('aria-hidden', 'true');
        svgElement.style.display = 'block';
        svgElement.style.overflow = 'visible';
      }
    } catch {
      removeElementChildren(container);
    }

    return () => {
      removeElementChildren(container);
    };
  }, [durationDivisions, fillColor, stemColor]);

  return (
    <Box
      ref={containerRef}
      aria-hidden="true"
      sx={{
        width: `${VEXFLOW_NOTE_ICON_WIDTH}px`,
        height: `${VEXFLOW_NOTE_ICON_HEIGHT}px`,
        overflow: 'visible',
        pointerEvents: 'none',
        color: fillColor,
        '& svg': {
          overflow: 'visible',
        },
      }}
    />
  );
};

type ScoreDragNoteGlyphProps = {
  durationDivisions: number;
  scale?: number;
  fillColor?: string;
  borderColor?: string;
  stemColor?: string;
  hollowFillColor?: string;
};

export const ScoreDragNoteGlyph = ({
  durationDivisions,
  scale = 1,
  fillColor = 'currentColor',
  stemColor = 'currentColor',
}: ScoreDragNoteGlyphProps) => (
  <Box
    aria-hidden="true"
    sx={{
      position: 'relative',
      width: 0,
      height: 0,
      pointerEvents: 'none',
    }}
  >
    <Box
      sx={{
        position: 'absolute',
        left: `${-VEXFLOW_NOTE_ANCHOR_X * scale}px`,
        top: `${-VEXFLOW_NOTE_ANCHOR_Y * scale}px`,
        width: `${VEXFLOW_NOTE_ICON_WIDTH * scale}px`,
        height: `${VEXFLOW_NOTE_ICON_HEIGHT * scale}px`,
        overflow: 'visible',
        transform: `scale(${scale})`,
        transformOrigin: 'top left',
      }}
    >
      <VexFlowRenderedNoteGlyph
        durationDivisions={durationDivisions}
        fillColor={fillColor}
        stemColor={stemColor}
      />
    </Box>
  </Box>
);

type NoteLengthIconProps = {
  durationDivisions: number;
  fillColor?: string;
  stemColor?: string;
};

export const NoteLengthIcon = ({
  durationDivisions,
  fillColor = 'currentColor',
  stemColor = fillColor,
}: NoteLengthIconProps) => (
  <Box
    data-live-draft-note-glyph
    aria-hidden="true"
    sx={{
      width: `${VEXFLOW_NOTE_ICON_WIDTH}px`,
      height: `${VEXFLOW_NOTE_ICON_HEIGHT}px`,
      color: fillColor,
      overflow: 'visible',
      pointerEvents: 'none',
    }}
  >
    <Box
      sx={{
        width: `${VEXFLOW_NOTE_ICON_WIDTH}px`,
        height: `${VEXFLOW_NOTE_ICON_HEIGHT}px`,
        transform: `scale(${NOTE_PALETTE_GLYPH_SCALE})`,
        transformOrigin: 'center center',
      }}
    >
      <VexFlowRenderedNoteGlyph
        durationDivisions={durationDivisions}
        fillColor={fillColor}
        stemColor={stemColor}
      />
    </Box>
  </Box>
);
